import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import CargomaticInput from 'components/v2/input';
import CustomerSearch from 'components/site/customer-search-alt';
import CargomaticSelect from 'components/v2/select';
import CargomaticTooltip from 'components/v2/Tooltip';
import InfoIcon from 'components/v2/icons/InfoIcon';
import DateTimeField from 'components/v2/DateTimeField';
import {
  Combobox,
  RadioGroup,
  RadioGroupItem,
  Skeleton,
  TableCell,
  TableRow,
} from '@cargomatic/ui';
import { ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import formatMoney from 'utils/formatters/Money';
import { validDecimal } from 'utils/accounting-util';
import ViewAccessorialRequestDocumentRequirements from 'components/modules/shipments/accessorials/ViewAccessorialDocumentRequirements';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import {
  getAutoUnitCalculation,
  getAppliesToFields,
} from 'utils/accessorials/unitCalculation';

class DrayageAccessorialRow extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedAccessorial: null,
      errors: {}, // Not directly used yet but allows flexibility for the future
      expanded: true, // Detail panel starts open; collapse to scan the list.
      // Draft text for the type Combobox while the user is typing to filter.
      // null = show the selected accessorial's label (derived from props), so
      // the field stays correct across re-renders/row reorders. base-ui's own
      // input state gets dropped when a row shifts (e.g. after a row above is
      // removed); controlling inputValue from props is what keeps it stable.
      typeInputDraft: null,
    };
  }

  toggleExpand = () => {
    this.setState((prev) => ({ expanded: !prev.expanded }));
  };

  static propTypes = {
    shipment: PropTypes.shape({
      _id: PropTypes.string.isRequired,
      shipment_id: PropTypes.string.isRequired,
      equipments: PropTypes.arrayOf(
        PropTypes.shape({
          equipment_id: PropTypes.string.isRequired,
          _id: PropTypes.string.isRequired,
        }),
      ),
      meta: PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string,
        charge_code: PropTypes.string,
        equipment: PropTypes.string,
        shipper_cost: PropTypes.string,
        carrier_cost: PropTypes.string,
        errors: PropTypes.object,
      }),
    }).isRequired,
    accessorials: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        _id: PropTypes.string,
        slug: PropTypes.string,
      }),
    ),
    onFieldChange: PropTypes.func.isRequired,
    onApplyAll: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    handleUpdateAccessorialRequiredDocs: PropTypes.func.isRequired,
    edit: PropTypes.bool,
    handleUpdateCustomerApproval: PropTypes.func,
  };

  static defaultProps = {
    accessorials: [],
    edit: false,
  };

  componentDidMount() {
    const {
      edit,
      shipment,
      accessorials,
      defaultAccessorialType = null,
    } = this.props;
    if (edit && shipment?.meta?.type) {
      const selectedAccessorial = accessorials.find(
        (a) => a.slug === shipment?.meta?.type,
      );
      this.setState({ selectedAccessorial }, () =>
        this.recomputeAutoUnits(this.getMeta()),
      );
    }
    if (!edit && defaultAccessorialType) {
      this.handleTypeChange(defaultAccessorialType);
    }
  }

  componentDidUpdate(prevProps) {
    const { edit, shipment, accessorials } = this.props;
    if (edit && prevProps?.shipment?.meta?.type !== shipment?.meta?.type) {
      const selectedAccessorial = accessorials.find(
        (a) => a.slug === shipment?.meta?.type,
      );
      this.setState({ selectedAccessorial }, () =>
        this.recomputeAutoUnits(this.getMeta()),
      );
    }
  }

  getJobId() {
    const {
      shipment: { _id },
      index,
    } = this.props;
    return `${_id}-${index}`;
  }

  getKey(prefix) {
    return `${prefix}-${this.getJobId()}`;
  }

  getShipmentId() {
    const {
      shipment: { shipment_id },
    } = this.props;

    return shipment_id;
  }

  getShipmentLink() {
    const {
      shipment: { _id },
    } = this.props;
    return `/shipments/${_id}`;
  }

  getMeta() {
    const {
      shipment: { meta },
    } = this.props;
    return meta || {};
  }

  // A unit field is system-managed when the selected accessorial has
  // auto-calculate on and Applies To covers that side. Read-only regardless of
  // whether the dates are filled in yet.
  isUnitsAutoManaged(side) {
    const accessorial = this.state.selectedAccessorial;
    if (!accessorial) return false;
    const fields = getAppliesToFields(accessorial.applies_to);
    return (
      !!accessorial.auto_calculate_units &&
      !!accessorial.calc_method &&
      fields[side]
    );
  }

  // Recomputes the auto-calculated unit field(s) from the row's Begin/End dates
  // and pushes them up via onFieldChange. Carrier units use the carrier dates;
  // shipper units use the shipper dates in edit mode, or the shared dates when
  // adding. Pure calc lives in getAutoUnitCalculation.
  recomputeAutoUnits(mergedMeta) {
    const { edit } = this.props;
    const accessorial = this.state.selectedAccessorial;
    if (!accessorial) return;

    const carrierCalc = getAutoUnitCalculation(accessorial, {
      beginDate: mergedMeta.begin_datetime,
      endDate: mergedMeta.end_datetime,
    });
    if (carrierCalc.active && carrierCalc.fields.carrier) {
      this.pushAutoUnits(
        'carrier_unit_qty',
        carrierCalc.units,
        mergedMeta.carrier_unit_qty,
      );
    }

    const shipperCalc = getAutoUnitCalculation(accessorial, {
      beginDate: edit
        ? mergedMeta.shipper_begin_datetime
        : mergedMeta.begin_datetime,
      endDate: edit ? mergedMeta.shipper_end_datetime : mergedMeta.end_datetime,
    });
    if (shipperCalc.active && shipperCalc.fields.shipper) {
      this.pushAutoUnits(
        'shipper_unit_qty',
        shipperCalc.units,
        mergedMeta.shipper_unit_qty,
      );
    }
  }

  // Pushes the auto-calculated units for a managed field. When no value is
  // computable yet (dates not both set), clears it to empty so the read-only
  // field never shows a misleading seeded number.
  pushAutoUnits(field, calcUnits, currentValue) {
    const { onFieldChange } = this.props;
    if (calcUnits === null) {
      if (currentValue !== '' && currentValue != null) {
        onFieldChange(field, '');
      }
      return;
    }
    if (Number(currentValue) !== calcUnits) {
      onFieldChange(field, calcUnits);
    }
  }

  handleDateFieldChange = (id) => (value) => {
    this.props.onFieldChange(id, value);
    this.recomputeAutoUnits({ ...this.getMeta(), [id]: value });
  };

  getEquipments() {
    return this.props?.shipment?.equipments || [];
  }

  getValidationClass(field) {
    const {
      shipment: {
        meta: { errors },
      },
    } = this.props;

    if (errors[field]) {
      return {
        error: true,
      };
    }
    return {
      error: false,
    };
  }

  handleApplyAll = () => this.props.onApplyAll();

  handleRemove = () => this.props.onRemove();

  invalidUnitQty = (field, value) =>
    field.indexOf('unit_qty') > -1 && !validDecimal(value);

  handleFieldChange = (field) => (value) => {
    // check whether it's a unit quantity field or not
    if (this.invalidUnitQty(field, value)) {
      // if it's a unit field but the input is invalid, skip
      return;
    }
    this.props.onFieldChange(field, value);
  };

  handleVendorSelect = (_value, item) => {
    this.props.onVendorFieldChange({
      vendor_name: item.company_name,
      vendor: item._id,
    });
  };

  handleVendorFieldChange = (value) => {
    this.props.onVendorFieldChange({
      vendor_name: value,
      vendor: null,
    });
  };

  handleEquipmentChange = (e) => {
    this.props.onFieldChange('equipment', e.target.value);
  };

  handleTypeChange = (type) => {
    const { accessorials, onFieldChange } = this.props;
    const selectedAccessorial = accessorials.find((a) => a.slug === type);
    onFieldChange('type', type);
    onFieldChange('charge_code', selectedAccessorial.charge_code);
    if (type && type !== 'other') {
      const name = accessorials.find((a) => a.slug === type).name;

      this.resetRequiredFieldsMeta(selectedAccessorial);
      this.setState(
        {
          selectedAccessorial,
        },
        () => {
          onFieldChange('name', name);
          // Dates were just reset; clear any seeded units on now-managed sides.
          this.recomputeAutoUnits({
            ...this.getMeta(),
            begin_datetime: null,
            end_datetime: null,
            shipper_begin_datetime: null,
            shipper_end_datetime: null,
          });
        },
      );
    } else {
      this.resetRequiredFieldsMeta(null);
      this.setState(
        {
          selectedAccessorial: null,
        },
        () => onFieldChange('name', ''),
      );
    }
  };

  handleCustomerApprovalChanges = (required, provided) => {
    this.props.handleUpdateCustomerApproval(required, provided);
  };

  renderShipmentLink() {
    const key = this.getKey('shipment-link');
    const shipmentLink = this.getShipmentLink();
    return (
      <TableCell key={key}>
        <div className="min-w-[70px]">
          <a href={shipmentLink} target="_blank" rel="noreferrer noopener">
            {this.getShipmentId()}
          </a>
        </div>
      </TableCell>
    );
  }

  handleLocationChange = (item, field) => {
    const { value } = item;
    this.props.onFieldChange(field, value);
  };

  renderEquipmentDropdown() {
    const { t, edit } = this.props;
    const key = this.getKey('equipments');
    const equipments = this.getEquipments();
    const meta = this.getMeta();
    const className = classnames({
      ...this.getValidationClass('equipment'),
    });

    return (
      <TableCell key={key}>
        <div className={classnames('min-w-[156px]', className)}>
          <select
            className="form-select"
            value={meta.equipment}
            onChange={this.handleEquipmentChange}
            disabled={edit}
          >
            <option>{t('SELECT_EQUIPMENTS')}</option>
            {equipments.map((equipment) => (
              <option key={equipment._id} value={equipment._id}>
                {equipment.equipment_id}
              </option>
            ))}
          </select>
        </div>
      </TableCell>
    );
  }

  renderLocation(field = 'location', disabled = false) {
    const { t, edit } = this.props;
    const meta = this.getMeta();
    const className = classnames({
      'mb-4': edit,
    });

    const { errors } = this.state;
    const { required_fields } = meta;
    const locationTypes = [
      {
        localizationSlug: 'CUSTOMER',
        value: 'customer',
      },
      {
        localizationSlug: 'TERMINAL',
        value: 'terminal',
      },
    ];
    if ((required_fields || []).includes('location_required')) {
      return (
        <div className={classnames('mt-[5px]', className)}>
          <CargomaticSelect
            selected={meta[field]?.toLowerCase()}
            fullWidth={false}
            items={locationTypes}
            valueKey="value"
            textKey="localizationSlug"
            error={errors[field]} // Not directly used yet but allows flexibility for the future
            defaultSelectLabel=""
            label={titleize(t('LOCATION'))}
            info={t('LOCATION_CUSTOMER_OR_TERMINAL')}
            onSelect={(item) => this.handleLocationChange(item, field)}
            showCaption
            disabled={disabled}
          />
        </div>
      );
    }
  }

  handleProcessingDocumentUpload = (uploading) => {
    if (uploading) console.log('accessorial document uploading');
    if (!uploading) console.log('accessorial document uploaded successfully');
  };

  renderRequiredAccessorialDocuments() {
    if (!this.state.selectedAccessorial) {
      return null;
    }
    return (
      <ViewAccessorialRequestDocumentRequirements
        containerClassName="mb-4 p-2"
        accessorial={this.state.selectedAccessorial}
        shipper={
          this.props.shipment?.shipper?._id || this.props.shipment?.shipper
        }
        shipment={this.props.shipment}
        handleCustomerApprovalChanges={this.handleCustomerApprovalChanges}
        handleUpdateAccessorialRequiredDocs={
          this.props.handleUpdateAccessorialRequiredDocs
        }
      />
    );
  }

  renderTypeDropdown() {
    const { t, edit } = this.props;
    const meta = this.getMeta();
    const className = classnames({
      ...this.getValidationClass('type'),
    });
    const options = this.props.accessorials.filter(
      (acc) => acc.slug !== 'other',
    );
    const val = options.find((a) => a.slug === meta.type) || null;
    const selectedLabel = val ? val.name : '';
    const { typeInputDraft } = this.state;
    return (
      <div className={classnames('min-w-[180px]', className)}>
        <Combobox
          className="mb-2"
          items={options}
          value={val}
          inputValue={typeInputDraft != null ? typeInputDraft : selectedLabel}
          onValueChange={(item) => {
            this.setState({ typeInputDraft: null });
            this.handleTypeChange(item ? item.slug : '');
          }}
          onInputValueChange={(v) => this.setState({ typeInputDraft: v })}
          itemToLabel={(a) => a.name}
          itemToValue={(a) => a.slug}
          placeholder={t('SELECT_ACCESSORIAL_TYPE')}
          disabled={edit}
          showClear={false}
        />
      </div>
    );
  }

  renderNameInput() {
    const meta = this.getMeta();
    const { type, name } = meta;
    const { t } = this.props;

    if (type !== 'other') {
      return null;
    }

    const className = classnames({
      ...this.getValidationClass('name'),
    });
    return (
      <div className={classnames('min-w-[120px]', className)}>
        <CargomaticInput
          className="mb-4"
          hideLabel
          maxLength={200}
          type="text"
          placeholder={t('ENTER_DESCRIPTION')}
          value={name}
          onChange={this.handleFieldChange('name')}
        />
      </div>
    );
  }

  renderName() {
    const key = this.getKey('name');
    return (
      <TableCell key={key}>
        <div>
          {this.renderTypeDropdown()}
          {this.renderNameInput()}
        </div>
      </TableCell>
    );
  }

  renderDescription(field = 'description', disabled = false) {
    const { edit } = this.props;
    const meta = this.getMeta();
    const { required_fields } = meta;
    const { t } = this.props;
    const descriptionClass = classnames('w-56 max-w-full', {
      'mb-4': edit,
    });
    if ((required_fields || []).includes('description_required')) {
      return (
        <div className={descriptionClass}>
          <label htmlFor="description" className="form-label">
            {titleize(t('DESCRIPTION', { count: 1 }))}
          </label>
          <CargomaticInput
            hideLabel
            maxLength={300}
            className="w-full"
            type="text"
            placeholder={titleize(t('DESCRIPTION', { count: 1 }))}
            onChange={this.handleFieldChange(field)}
            value={meta[field]}
            disabled={disabled}
          />
        </div>
      );
    }
  }

  // A single @cargomatic/ui radio option. When `info` is set (e.g. a disabled
  // reason), the label text + an info icon become the tooltip trigger so the
  // explanation shows on hover over the whole label, not just the icon.
  renderRadioItem(value, id, label, opts = {}) {
    const { disabled, info } = opts;
    return (
      <div className="flex items-center gap-2">
        <RadioGroupItem
          value={value}
          id={id}
          disabled={disabled}
          aria-label={label}
        />
        {info ? (
          <CargomaticTooltip
            id={info}
            body={() => (
              <span className="flex items-center gap-1 text-sm font-medium text-gray-400 cursor-help">
                {label}
                <InfoIcon className="w-4 h-4" />
              </span>
            )}
          >
            {info}
          </CargomaticTooltip>
        ) : (
          <label
            htmlFor={id}
            className="text-sm font-medium text-gray-700 cursor-pointer"
          >
            {label}
          </label>
        )}
      </div>
    );
  }

  renderVendor() {
    const meta = this.getMeta();
    const { payee_type } = meta;
    const key = this.getKey('vendor');
    const { carrierEnabled, t, edit } = this.props;

    const jobId = this.getJobId();
    const carrierId = `carrier-${jobId}`;
    const vendorId = `vendor-${jobId}`;
    return (
      <TableCell className={key}>
        <RadioGroup
          value={payee_type}
          onValueChange={this.handleFieldChange('payee_type')}
          className="flex flex-col gap-2"
        >
          {this.renderRadioItem(
            'carrier',
            carrierId,
            titleize(t('CARRIER', { count: 1 })),
            {
              disabled: edit || !carrierEnabled,
              info: !carrierEnabled
                ? t(
                    'CARRIER_ACCESSORIAL_ERROR',
                    'This shipment has no carrier assigned.',
                  )
                : '',
            },
          )}
          {this.renderRadioItem(
            'vendor',
            vendorId,
            titleize(t('VENDOR', { count: 1 })),
            { disabled: edit },
          )}
        </RadioGroup>
      </TableCell>
    );
  }

  // Vendor search lives in the expanded detail (not the cramped summary cell);
  // only relevant when the payee is a vendor.
  renderVendorSearch() {
    const { t, edit } = this.props;
    const { vendor_name } = this.getMeta();
    return (
      <div className="w-56 max-w-full">
        <label className="mb-1 form-label">{t('SEARCH_VENDORS')}</label>
        <CustomerSearch
          clearOnSelect={false}
          onChange={this.handleVendorFieldChange}
          onSelect={this.handleVendorSelect}
          placeholder={t('SEARCH_FOR_VENDOR')}
          type="vendors"
          authorizedOnly={false}
          value={vendor_name}
          disabled={edit}
        />
      </div>
    );
  }

  // Begin/End only render when the selected accessorial config requires them
  // (required_fields). Returns null otherwise so non-required dates never show.
  renderDate(id /* accessorial */, disabled = false) {
    const { t, edit } = this.props;
    const meta = this.getMeta();
    const reqs = meta.required_fields || [];
    const isBegin = id === 'begin_datetime' || id === 'shipper_begin_datetime';
    const isEnd = id === 'end_datetime' || id === 'shipper_end_datetime';
    const showBegin = isBegin && reqs.includes('begin_date_required');
    const showEnd = isEnd && reqs.includes('end_date_required');
    if (!showBegin && !showEnd) {
      return null;
    }
    const dateClass = classnames('w-full sm:w-auto sm:min-w-[300px]', {
      'mb-4': edit,
    });
    return (
      <div className={dateClass} key={id}>
        <label
          htmlFor={showBegin ? 'startDate' : 'endDate'}
          className="form-label"
        >
          {showBegin ? t('BEGIN_DATE') : t('END_DATE')}
        </label>
        <DateTimeField
          className="!flex-nowrap [&>*]:flex-1"
          dateTestId={`${id}-date`}
          timeTestId={`${id}-time`}
          value={meta[id] || null}
          onChange={this.handleDateFieldChange(id)}
          disabled={disabled}
          timeIntervals={5}
        />
      </div>
    );
  }

  renderShipperCost() {
    const { t } = this.props;
    const meta = this.getMeta();
    const { shipper_cost, disable_payer, ratesRequestPending } = meta;
    const key = this.getKey('shipper_cost');
    const className = classnames({
      ...this.getValidationClass('shipper_cost'),
    });
    return (
      <TableCell key={key}>
        <div className={classnames('min-w-[126px]', className)}>
          {ratesRequestPending ? (
            <Skeleton className="h-9 w-full rounded-md" />
          ) : (
            <CargomaticInput
              className="mb-0"
              hideLabel
              maxLength={6}
              type="number"
              placeholder={titleize(t('SHIPPER_RATE'))}
              value={shipper_cost}
              disabled={disable_payer}
              onChange={this.handleFieldChange('shipper_cost')}
            />
          )}
        </div>
      </TableCell>
    );
  }

  renderCarrierCost() {
    const { t } = this.props;
    const meta = this.getMeta();
    const {
      carrier_cost,
      disable_payee,
      ratesRequestPending,
      isVendorPaid = false,
    } = meta;
    const key = this.getKey('carrier_cost');
    const className = classnames({
      ...this.getValidationClass('carrier_cost'),
    });
    return (
      <TableCell key={key}>
        <div className={classnames('min-w-[126px]', className)}>
          {ratesRequestPending ? (
            <Skeleton className="h-9 w-full rounded-md" />
          ) : (
            <CargomaticInput
              className="mb-0"
              hideLabel
              maxLength={6}
              type="number"
              placeholder={titleize(t('CARRIER_COST'))}
              value={carrier_cost}
              onChange={this.handleFieldChange('carrier_cost')}
              disabled={disable_payee || isVendorPaid}
            />
          )}
        </div>
      </TableCell>
    );
  }

  renderUnits(type, disabled = false) {
    const { t } = this.props;
    const meta = this.getMeta();
    const key = this.getKey(`${type}_unit_qty`);
    const autoManaged = this.isUnitsAutoManaged(type);
    const className = classnames({
      ...this.getValidationClass(type),
    });

    return (
      <TableCell key={key}>
        <div className={classnames('min-w-[120px]', className)}>
          <CargomaticInput
            className="mb-0"
            hideLabel
            type="number"
            min="0"
            step="0.5"
            placeholder={
              autoManaged ? t('AUTO_CALCULATED', 'Auto-calculated') : undefined
            }
            value={meta[`${type}_unit_qty`]}
            onChange={this.handleFieldChange(`${type}_unit_qty`)}
            disabled={disabled || autoManaged}
          />
        </div>
      </TableCell>
    );
  }

  renderTotal(type) {
    const meta = this.getMeta();
    const key = this.getKey(`${type}_total`);
    const rate = parseFloat(meta[`${type}_cost`]);
    const units = parseFloat(meta[`${type}_unit_qty`]);

    const total = (rate || 0) * (units || 1);

    return (
      <TableCell key={key}>
        <div>
          <p className="min-w-[75px] max-w-[75px]">
            {formatMoney(total || 0, this.props.shipment?.currency)}
          </p>
        </div>
      </TableCell>
    );
  }

  // Notes is the one always-on field; it has no config gate. Per-side note keys
  // (shipper_note/carrier_note) are used in edit mode, plain `notes` when adding.
  renderNotesInput(input_type = 'notes', disabled = false) {
    const { t } = this.props;
    const meta = this.getMeta();
    const className = classnames('w-56 max-w-full', {
      ...this.getValidationClass('notes'),
    });
    return (
      <div className={className} key={this.getKey(input_type)}>
        <label htmlFor={input_type} className="form-label">
          {titleize(t('NOTE', { count: 2 }))}
        </label>
        <CargomaticInput
          className="mb-0"
          hideLabel
          maxLength={300}
          type="text"
          placeholder={titleize(t('NOTE', { count: 2 }))}
          onChange={this.handleFieldChange(input_type)}
          value={meta[input_type]}
          disabled={disabled}
        />
      </div>
    );
  }

  renderApplyAll() {
    const key = this.getKey('apply-all');
    const { t, disabledApplyBtn } = this.props;
    return (
      <TableCell key={key}>
        <button
          type="button"
          className="button button-hollow-green min-w-[80px]"
          disabled={disabledApplyBtn}
          onClick={this.handleApplyAll}
        >
          {titleize(t('APPLY'))}
        </button>
      </TableCell>
    );
  }

  resetRequiredFieldsMeta(selectedAccessorial) {
    this.props.onFieldChange('location', null);
    this.props.onFieldChange('begin_datetime', null);
    this.props.onFieldChange('end_datetime', null);
    this.props.onFieldChange('description', null);
    if (!selectedAccessorial) {
      this.props.onFieldChange('required_fields', null);
    } else {
      const required_fields = [];
      if (selectedAccessorial.begin_date_required) {
        required_fields.push('begin_date_required');
      }
      if (selectedAccessorial.end_date_required) {
        required_fields.push('end_date_required');
      }
      if (selectedAccessorial.location_required) {
        required_fields.push('location_required');
      }
      if (selectedAccessorial.description_required) {
        required_fields.push('description_required');
      }
      this.props.onFieldChange('required_fields', required_fields);
    }
  }

  renderActions() {
    const { edit } = this.props;
    const key = this.getKey('remove');
    const id = this.getJobId();

    return (
      <TableCell key={key}>
        <div className="flex justify-center">
          <button
            type="button"
            onClick={this.handleRemove}
            data-testid="drayageAccessorialRowRemoveButton"
          >
            <X className="h-4 w-4" />
          </button>
          {!edit && (
            <button
              data-testid="drayageAccessorialRowOnRowAddButton"
              className="ml-2"
              type="button"
              onClick={() => this.props.onRowAdd(id)}
            >
              <Plus className="h-4 w-4 text-green" />
            </button>
          )}
        </div>
      </TableCell>
    );
  }

  renderExpandToggle() {
    const { expanded } = this.state;
    return (
      <TableCell key={this.getKey('expand')} className="align-middle">
        <button
          type="button"
          onClick={this.toggleExpand}
          aria-label={expanded ? 'Collapse details' : 'Expand details'}
          data-testid="drayageAccessorialRowExpandToggle"
          className="text-gray-500 hover:text-gray-800"
        >
          {expanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
      </TableCell>
    );
  }

  // The expanded detail behaves like a normal form (full width), distinguished
  // by a light-gray bordered panel, holding the fields that don't belong in the
  // scannable summary row. Every optional field (Begin/End date, Location,
  // Description) is gated on the selected accessorial's config via its render
  // helper, so only fields the accessorial actually requires are shown; Notes is
  // the one always-on field. In edit mode the fields are split per side — the
  // admin/payer side uses the shipper_* keys (disabled when the payer is locked)
  // and the carrier/payee side uses the carrier-side keys.
  renderDetailPanel() {
    const { t, edit } = this.props;
    const { disable_payer, disable_payee, isVendorPaid } = this.getMeta();
    const carrierDisabled = disable_payee || isVendorPaid;
    return (
      <div className="p-4">
        {edit ? (
          <div className="flex flex-wrap gap-x-12 gap-y-4">
            <div className="flex flex-col gap-2">
              <span className="form-label !mb-0 font-semibold uppercase text-gray-500">
                {titleize(t('ADMIN'))}
              </span>
              <div className="flex flex-wrap items-start gap-x-6 gap-y-4">
                {this.renderDate('shipper_begin_datetime', disable_payer)}
                {this.renderDate('shipper_end_datetime', disable_payer)}
                {this.renderLocation('shipper_location', disable_payer)}
                {this.renderDescription('shipper_description', disable_payer)}
                {this.renderNotesInput('shipper_note', disable_payer)}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span className="form-label !mb-0 font-semibold uppercase text-gray-500">
                {titleize(t('CARRIER', { count: 1 }))}
              </span>
              <div className="flex flex-wrap items-start gap-x-6 gap-y-4">
                {this.renderDate('begin_datetime', carrierDisabled)}
                {this.renderDate('end_datetime', carrierDisabled)}
                {this.renderLocation('location', carrierDisabled)}
                {this.renderDescription('description', carrierDisabled)}
                {this.renderNotesInput('carrier_note', carrierDisabled)}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-start gap-x-6 gap-y-4">
            {this.getMeta().payee_type === 'vendor' &&
              this.renderVendorSearch()}
            {this.renderDate('begin_datetime')}
            {this.renderDate('end_datetime')}
            {this.renderLocation('location')}
            {this.renderDescription('description')}
            {this.renderNotesInput('notes')}
          </div>
        )}
        {featureEnabled(CONFIGURED_FEATURES.SSR) &&
          this.state.selectedAccessorial && (
            <>
              <hr className="my-4 border-gray-200" />
              <div className="max-w-[33%] text-sm [&_th]:h-auto [&_th]:whitespace-normal [&_th]:px-2 [&_th]:py-2 [&_td]:whitespace-normal [&_td]:px-2 [&_td]:py-1">
                {this.renderRequiredAccessorialDocuments()}
              </div>
            </>
          )}
      </div>
    );
  }

  render() {
    const { edit } = this.props;
    const { disable_payer, disable_payee, isVendorPaid } = this.getMeta();
    const { expanded } = this.state;
    return (
      <>
        <TableRow className="w-full">
          {this.renderExpandToggle()}
          {this.renderShipmentLink()}
          {this.renderEquipmentDropdown()}
          {this.renderName()}
          {this.renderVendor()}
          {this.renderShipperCost()}
          {this.renderUnits('shipper', disable_payer)}
          {this.renderTotal('shipper')}
          {this.renderCarrierCost()}
          {this.renderUnits('carrier', disable_payee || isVendorPaid)}
          {this.renderTotal('carrier')}
          {!edit && this.renderApplyAll()}
          {this.renderActions()}
        </TableRow>
        {expanded && (
          <TableRow className="w-full hover:bg-transparent">
            <TableCell colSpan={13} className="p-2">
              {/* Pin to the left + cap at viewport width so the expanded
                  form (incl. the full-width docs list) never forces an hscroll
                  even when the summary table is wider than the modal. */}
              <div className="sticky left-0 max-w-[calc(95vw-6rem)]">
                {this.renderDetailPanel()}
              </div>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  }
}

export default withTranslation()(DrayageAccessorialRow);
